import React, { useState } from 'react';
import { Presentation, Download, FileText } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, NoteCard } from '../shared';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import JSZip from 'jszip';
import '../../styles/design-system.css';

// Set up PDF.js worker
GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PDFtoPOWERPOINT = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [pageCount, setPageCount] = useState(0);

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const loadingTask = getDocument({ data: arrayBuffer });
                const pdf = await loadingTask.promise;
                setPageCount(pdf.numPages);
                toast.success(`PDF loaded: ${pdf.numPages} pages`);
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    const convertToPPTX = async () => {
        if (!file) return;

        try {
            setIsProcessing(true);
            setProgress(10);

            const arrayBuffer = await file.arrayBuffer();
            const loadingTask = getDocument({ data: arrayBuffer });
            const pdf = await loadingTask.promise;

            setProgress(30);

            const zip = new JSZip();
            const slideImages = [];

            // Convert each page to image
            for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                const page = await pdf.getPage(pageNum);
                const viewport = page.getViewport({ scale: 2.0 });

                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                canvas.width = viewport.width;
                canvas.height = viewport.height;

                await page.render({
                    canvasContext: context,
                    viewport: viewport
                }).promise;

                const imageData = canvas.toDataURL('image/png');
                const base64Data = imageData.split(',')[1];

                zip.file(`slide${pageNum}.png`, base64Data, { base64: true });
                slideImages.push(`slide${pageNum}.png`);

                setProgress(30 + ((pageNum / pdf.numPages) * 60));
            }

            // Create a simple text file with instructions
            const readme = `PDF to PPTX Conversion
      
This ZIP contains ${pdf.numPages} slide images extracted from your PDF.

To create a PowerPoint presentation:
1. Open PowerPoint
2. Create a new presentation
3. For each image:
   - Insert > Pictures
   - Select the slide image
   - Resize to fit the slide
4. Save as PPTX

Slide images included:
${slideImages.map((img, i) => `- ${img} (Page ${i + 1})`).join('\n')}

Note: This is a basic conversion. For production-quality PPTX with editable text,
consider using specialized conversion services.
`;

            zip.file('README.txt', readme);

            setProgress(95);

            const zipBlob = await zip.generateAsync({ type: 'blob' });

            setProgress(100);

            const fileName = file.name.replace(/\.pdf$/i, '');
            saveAs(zipBlob, `${fileName}-slides.zip`);

            toast.success('PDF pages exported as slide images!');
            toast.info('Import the images into PowerPoint to create your presentation.');

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('Conversion error:', error);
            toast.error(`Failed to convert: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {/* Header */}
            <div className="tool-header">
                <h1 className="tool-title">
                    <Presentation size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    PDF to POWERPOINT
                </h1>
                <p className="tool-description">
                    Convert PDF pages to PowerPoint slides. Each page becomes a slide image that can be imported into PowerPoint.
                </p>
            </div>

            {/* File Upload */}
            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                            {pageCount > 0 && <Badge variant="info">{pageCount} pages</Badge>}
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setFile(null);
                                setPageCount(0);
                            }}
                        >
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && (
                <>
                    {/* Info */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{
                                fontSize: 'var(--text-lg)',
                                fontWeight: 'var(--font-bold)',
                                marginBottom: 'var(--spacing-3)'
                            }}>
                                How It Works
                            </h3>
                            <ol style={{
                                fontSize: 'var(--text-sm)',
                                color: 'var(--color-text-secondary)',
                                lineHeight: 1.6,
                                paddingLeft: 'var(--spacing-5)'
                            }}>
                                <li>Each PDF page is converted to a high-quality PNG image</li>
                                <li>All images are packaged in a ZIP file</li>
                                <li>Import images into PowerPoint to create slides</li>
                                <li>Images can be used as slide backgrounds or content</li>
                            </ol>
                        </Card>
                    </div>

                    {/* Progress Bar */}
                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    {/* Convert Button */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={convertToPPTX}
                            disabled={isProcessing}
                            loading={isProcessing}
                            fullWidth
                        >
                            <Download size={20} />
                            Convert to Slide Images
                        </Button>
                    </div>

                    {/* Info */}
                    <NoteCard variant="info">
                        <strong>Note:</strong> This creates slide images, not an editable PPTX file. For fully editable presentations with text and objects, consider using specialized conversion services or Microsoft's official APIs.
                    </NoteCard>
                </>
            )}
        </div>
    );
};

export default PDFtoPOWERPOINT;
